// FE-PINV-072 — sticky bulk-action bar for the List view.
//
// Appears at the bottom of the viewport when ≥1 row is selected. Six
// canonical bulk actions per spec:
//   1. Move stage         — bulk endpoint (FE-PINV-072 BE; transactional)
//   2. Assign producer    — PATCH per row, sequential fan-out
//   3. Apply playbook     — opens W2's <ApplyPlaybookModal mode='bulk'>
//   4. Add to list        — saves new SavedView with augmented filter
//   5. Bulk Log Touchpoint — opens W5's <ActivityComposerModal> in bulk
//   6. Archive            — confirm + DELETE per row
//
// The bar itself is presentation-only; the host wires action handlers
// because Apply Playbook + Add to list + Bulk Log Touchpoint all open
// modals the host already owns.

import { useState } from "react";

import type { ListSelectionApi } from "./useListSelection";


export interface BulkActionBarProps {
  selection: ListSelectionApi;
  /** Available forward stages for the Move stage menu. The host
   *  filters terminals out for non-BOR opportunities to avoid the
   *  per-row validation error path. */
  moveStageOptions: { value: string; label: string }[];
  onMoveStage: (target_stage: string) => void;
  onAssignProducer: () => void;
  onApplyPlaybook: () => void;
  onAddToList: () => void;
  onBulkLogTouchpoint: () => void;
  onArchive: () => void;
  /** Optional pending mutation indicator (e.g. the bulk Move stage
   *  call is in flight). Disables the bar to prevent double-submit. */
  isPending?: boolean;
}


export function BulkActionBar({
  selection,
  moveStageOptions,
  onMoveStage,
  onAssignProducer,
  onApplyPlaybook,
  onAddToList,
  onBulkLogTouchpoint,
  onArchive,
  isPending = false,
}: BulkActionBarProps): JSX.Element | null {
  const [movePopoverOpen, setMovePopoverOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  if (selection.count === 0) return null;

  return (
    <div
      data-testid="bulk-action-bar"
      className={[
        "sticky bottom-2 z-20 flex items-center gap-3 rounded-md",
        "border border-border-default bg-raised px-3 py-2 shadow-overlay",
        isPending ? "opacity-60 pointer-events-none" : "",
      ].join(" ")}
      role="region"
      aria-label="Bulk actions"
    >
      <span
        className="text-xs font-medium text-text-primary"
        data-testid="bulk-action-count"
      >
        {selection.count} selected
      </span>
      <button
        type="button"
        onClick={selection.clear}
        data-testid="bulk-action-clear"
        className="text-xs text-text-tertiary underline-offset-2 hover:underline"
      >
        Clear
      </button>

      <div className="relative">
        <button
          type="button"
          onClick={() => setMovePopoverOpen((o) => !o)}
          data-testid="bulk-action-move-stage"
          className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-primary hover:border-text-tertiary"
        >
          Move stage
        </button>
        {movePopoverOpen ? (
          <ul
            data-testid="bulk-action-move-stage-menu"
            className="absolute bottom-full left-0 z-30 mb-1 w-48 rounded-md border border-border-default bg-raised shadow-overlay"
          >
            {moveStageOptions.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => {
                    setMovePopoverOpen(false);
                    onMoveStage(opt.value);
                  }}
                  data-testid={`bulk-action-move-stage-${opt.value}`}
                  className="block w-full px-2 py-1 text-left text-xs text-text-primary hover:bg-recessed"
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onAssignProducer}
        data-testid="bulk-action-assign-producer"
        className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-primary hover:border-text-tertiary"
      >
        Assign producer
      </button>

      <button
        type="button"
        onClick={onApplyPlaybook}
        data-testid="bulk-action-apply-playbook"
        className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-primary hover:border-text-tertiary"
      >
        Apply playbook
      </button>

      <button
        type="button"
        onClick={onAddToList}
        data-testid="bulk-action-add-to-list"
        className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-primary hover:border-text-tertiary"
      >
        Add to list
      </button>

      <button
        type="button"
        onClick={onBulkLogTouchpoint}
        data-testid="bulk-action-log-touchpoint"
        className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-primary hover:border-text-tertiary"
      >
        Bulk Log Touchpoint
      </button>

      <button
        type="button"
        onClick={() => setConfirmArchive(true)}
        data-testid="bulk-action-archive"
        className="rounded-md border border-rose bg-rose-tint px-2 py-1 text-xs text-text-primary hover:border-rose"
      >
        Archive
      </button>

      {confirmArchive ? (
        <div
          role="dialog"
          aria-label="Confirm archive"
          data-testid="bulk-action-archive-confirm"
          className="absolute right-0 bottom-full mb-2 flex flex-col gap-2 rounded-md border border-border-default bg-raised p-3 text-xs shadow-overlay"
        >
          <p className="text-text-primary">
            Archive {selection.count}{" "}
            {selection.count === 1 ? "opportunity" : "opportunities"}?
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              data-testid="bulk-action-archive-cancel"
              onClick={() => setConfirmArchive(false)}
              className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="bulk-action-archive-confirm-submit"
              onClick={() => {
                setConfirmArchive(false);
                onArchive();
              }}
              className="rounded-md bg-rose px-2 py-1 text-xs font-medium text-white"
            >
              Archive
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}


export const BULK_MOVE_STAGE_OPTIONS_FORWARD: {
  value: string; label: string;
}[] = [
  { value: "lead", label: "Lead" },
  { value: "meeting_set", label: "Meeting set" },
  { value: "qualified", label: "Qualified" },
  { value: "proposal", label: "Proposal" },
  { value: "negotiation", label: "Negotiation" },
];
